/**
 * SOLID Review
 * S — Strong. One use case: the name modal's submit — the guest created and seated, then the entry goes on.
 * O — Strong. The round trip runs it; what a name may be is the Domain's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The session's deps and the entry route, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandCreateGuest, ICommandGuestCreated } from '../../domain/ports/schemas/SessionMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import { DONE } from '../shared/ClientSteps';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepSession } from './IConfigDepSession';
import type { SessionEntryRoute } from './SessionEntryRoute';

export interface IConfigDepCreateGuest extends IConfigDepSession {
  readonly route: SessionEntryRoute;
}

type TGuestRequest = ICommandGuestCreated['request'];
type TGuestCreated = TAnswered<ICommandGuestCreated['player']>;

/**
 * THE NAME MODAL'S SUBMIT (FE2's IdentityModal): create the guest, seat the tab as them, then go on with the entry. An answer
 * that arrives after the caller's deadline is refused by the decisions port's guard (`DEADLINE`), so a modal that stopped
 * waiting is not identified behind its back.
 */
export class CreateGuestUseCase implements IUseCase<ICommandCreateGuest, TPortOutcome>, TServerRoundTrip<IPropsClientInput, ICommandCreateGuest, TGuestRequest, TGuestCreated> {
  constructor(private readonly deps: IConfigDepCreateGuest) {}

  async execute(ctx: IContextRequest, command: ICommandCreateGuest): Promise<TPortOutcome> {
    const seated = await this.deps.trip.run(ctx, command, this);
    if (!seated.success) return seated;
    await this.deps.route.proceed(ctx, false);
    return DONE;
  }

  plan(ctx: IContextRequest, input: IPropsClientInput, command: ICommandCreateGuest) {
    return this.deps.decisions.planGuest(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: TGuestRequest) {
    return this.deps.players.createPlayer(ctx, plan);
  }

  answer(ctx: IContextRequest, input: IPropsClientInput, plan: TGuestRequest, created: TGuestCreated) {
    const answer: ICommandGuestCreated = { request: plan, player: created.value };
    return this.deps.decisions.guestCreated(ctx, input, answer);
  }
}
